import React, { useEffect } from "react";

import {
  Stack,
  Text,
  Paper,
  Group,
  Button,
  Loader,
  Title,
  SegmentedControl,
  ThemeIcon,
} from "@mantine/core";
import { IconLinkOff, IconInfoCircle } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";

import LinkChatModal from "../components/LinkChatModal";
import useApi from "../utils/api/useApi";
import coreApi from "../utils/api/coreApi";
import { Chat, ChatStateType } from "../utils/api/api_types";
import { useState } from "react";
import { STANDARD_ERROR_MESSAGE } from "../utils/constants";
import {
  ChatProviderIcon,
  ChatProviderColor,
  ChatStateEmoji,
} from "../utils/enum_records";
import UnlinkChatModal from "../components/UnlinkChatModal";

export default function ManageNotifications() {
  const [
    openedChatUnlinkModal,
    { open: openChatUnlinkModal, close: closeChatUnlinkModal },
  ] = useDisclosure(false);
  const [chatToUnlink, setChatToUnlink] = useState<Chat | null>(null);

  const [
    openedChatLinkModal,
    { open: openChatLinkModal, close: closeChatLinkModal },
  ] = useDisclosure(false);

  const chatsRes = useApi<Chat[]>({
    apiFunc: coreApi.chatsList,
    defaultData: [],
    paginated: false,
  });
  const [chatsData, setChatsData] = useState<Chat[]>(
    chatsRes.responseData.data
  );
  const responseChatsJson = JSON.stringify(chatsRes.responseData.data);
  useEffect(() => {
    setChatsData(JSON.parse(responseChatsJson) as Chat[]);
  }, [responseChatsJson]);

  const [loadingToggleStateIndices, setLoadingToggleStateIndices] = useState<
    number[]
  >([]);

  const updateNotifications = async (
    chat: Chat,
    index: number,
    changes: Partial<Pick<Chat, "state" | "health_check_notifications" | "audit_notifications">>
  ) => {
    const updated = { ...chat, ...changes };
    setLoadingToggleStateIndices((prev) => [...prev, index]);
    setChatsData((current) => current.map((item, itemIndex) =>
      itemIndex === index ? updated : item));

    try {
      const res = await coreApi.chatsUpdateNotifications(chat.id.toString(), {
        overall_enabled: updated.state === ChatStateType.ACTIVE,
        health_checks_enabled: updated.health_check_notifications,
        audit_enabled: updated.audit_notifications,
      });
      setChatsData((current) => current.map((item, itemIndex) =>
        itemIndex === index ? res.chat : item));
      notifications.show({
        title: "Notifications Updated",
        message: "Notification preferences were saved",
        color: "#326950",
      });
    } catch {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
      setChatsData((current) => current.map((item, itemIndex) =>
        itemIndex === index ? chat : item));
    } finally {
      setLoadingToggleStateIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  return (
    <>
      <LinkChatModal
        opened={openedChatLinkModal}
        onClose={closeChatLinkModal}
        onSuccess={chatsRes.refresh}
      />
      <UnlinkChatModal
        chat={chatToUnlink}
        opened={openedChatUnlinkModal}
        onClose={() => {
          setChatToUnlink(null);
          closeChatUnlinkModal();
        }}
        onSuccess={chatsRes.refresh}
      />
      {/* Main Content */}
      <Stack align="center">
        <Title order={3} mt="xl">
          Manage Notifications
        </Title>

        {chatsRes.loading ? (
          <Group gap="md" mt="xl" justify="center">
            <Loader />
            <Text>Loading...</Text>
          </Group>
        ) : chatsData.length > 0 ? (
          <Stack align="center" mb="md">
            {chatsData.map((chat, index) => (
              <Paper p="md" w={{ base: "100%", xs: 400 }} key={chat.id}>
                <Stack>
                  <Group justify="space-between">
                    <Stack gap={0}>
                      <Group>
                        {ChatProviderIcon[chat.provider] &&
                          React.createElement(ChatProviderIcon[chat.provider], {
                            size: 30,
                            color: ChatProviderColor[chat.provider],
                          })}
                        <Text>{chat.provider}</Text>
                      </Group>
                      <Text c="dimmed" size="sm">
                        {`${chat.name} ${
                          chat.number !== null
                            ? `(${chat.number})`
                            : `(no username)`
                        }`}
                      </Text>
                    </Stack>
                    {loadingToggleStateIndices.includes(index) ? (
                      <Loader size="sm" />
                    ) : (
                      ChatStateEmoji[chat.state]
                    )}
                  </Group>

                  <Stack gap="xs">
                    <Text fw={600}>Overall</Text>
                    <SegmentedControl
                      transitionDuration={500}
                      disabled={loadingToggleStateIndices.includes(index)}
                      value={chat.state}
                      onChange={(value) => updateNotifications(chat, index, {
                        state: value as ChatStateType,
                      })}
                      data={[
                        { label: "ON", value: "ACTIVE" },
                        { label: "OFF", value: "INACTIVE" },
                      ]}
                    />
                    <Text fw={600} mt="xs">Health Checks</Text>
                    <SegmentedControl
                      disabled={loadingToggleStateIndices.includes(index) ||
                        chat.state !== ChatStateType.ACTIVE}
                      value={chat.health_check_notifications ? "ON" : "OFF"}
                      onChange={(value) => updateNotifications(chat, index, {
                        health_check_notifications: value === "ON",
                      })}
                      data={[{ label: "ON", value: "ON" }, { label: "OFF", value: "OFF" }]}
                    />
                    <Text fw={600} mt="xs">Audit</Text>
                    <SegmentedControl
                      disabled={loadingToggleStateIndices.includes(index) ||
                        chat.state !== ChatStateType.ACTIVE}
                      value={chat.audit_notifications ? "ON" : "OFF"}
                      onChange={(value) => updateNotifications(chat, index, {
                        audit_notifications: value === "ON",
                      })}
                      data={[{ label: "ON", value: "ON" }, { label: "OFF", value: "OFF" }]}
                    />
                  </Stack>
                  <Group gap="xs">
                    <Button
                      leftSection={<IconLinkOff size={20} />}
                      color="#fb8080"
                      fullWidth
                      variant="light"
                      onClick={() => {
                        setChatToUnlink(chat);
                        openChatUnlinkModal();
                      }}
                    >
                      Unlink Chat
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            ))}
            <Paper my="md" p="md" w={{ base: "100%", xs: 400 }}>
              <Stack>
                <Group gap="xs">
                  <ThemeIcon variant="light">
                    <IconInfoCircle size={"1.2rem"} />
                  </ThemeIcon>
                  <Text fw={700}>Info</Text>
                </Group>
                <Text size="md">
                  At the moment, we only allow 1 phone per account.
                </Text>
              </Stack>
            </Paper>
          </Stack>
        ) : (
          <Paper my="md" p="md" w={{ base: "100%", xs: 400 }}>
            <Group wrap="nowrap" justify="space-between">
              <Text>No Phones Linked 📵</Text>
              <Button onClick={openChatLinkModal} variant="outline">
                Link Phone
              </Button>
            </Group>
          </Paper>
        )}
      </Stack>
    </>
  );
}
